import React from 'react';
import { PropTypes } from 'prop-types';
import {
  MenuItem, FormControl, TextField
} from '@mui/material';
import { Controller, get } from 'react-hook-form';

import useRemoveSpaces from '../utils/removeSpaces';
import getFormControllerRules from '../utils/getFormControllerRules';

/**
 * Reusable Autocomplete for use with react-hook-form.
 *
 * @param {object} control - React hook form control.
 * @param {object} formErrors - React hook form formErrors.
 * @param {string} label - Label to be used for the field as well as test id and error messages.
 * @param {string} formFieldId - Id to be used with react-hook-form.
 * @param {boolean} required - Is the field required?
 * @param {boolean} readonly - Is the field read only?
 * @param {Array} options - Options to display in the dropdown.
 * @param {object} textFieldProps - Accepts all TextField props.
 * @param {string} testId - Alternative testId label to overwrite label.
 * @param {object} validationOptions - React Hook Form validation options.
 * @param {boolean} showInlineErrors - Should the field show errors in help text? (defaults to `true`).
 * @param {object} rest - Also accepts all Autocomplete props.
 * @param {string} valueName - Field of value in object array for display purposes.
 */
const FormDropDown = ({
  control,
  formErrors,
  label,
  options,
  formFieldId,
  required,
  readonly,
  testId,
  validationOptions,
  valueName,
  showInlineErrors = true,
  textFieldProps,
  ...rest
}) => {
  const testIdMemo = useRemoveSpaces(testId ?? `${ label }_Field`);
  const rules = getFormControllerRules(label, required, validationOptions);
  const modifiedHelperText = (showInlineErrors && get(formErrors, formFieldId)?.message) || textFieldProps?.helperText;

  return (
    <Controller
      name={formFieldId}
      control={control}
      rules={rules}
      render={({ field: { ref, value, ...field } }) => (
        <FormControl fullWidth error={!!get(formErrors, formFieldId)}>
          <TextField
            select
            fullWidth
            data-testid={testIdMemo}
            inputRef={ref}
            required={required}
            label={label}
            error={!!get(formErrors, formFieldId)}
            helperText={modifiedHelperText}
            value={value ?? ''}
            {...rest}
            {...field}
          >
            {options && options.map((option) => (
              typeof option === 'object' ? (
                <MenuItem key={option.id} value={valueName ? option[valueName] : option.value}>
                  {valueName ? option[valueName] : option.value} {/* Dynamically display the name based on valueName prop */}
                </MenuItem>
              ) : (
                <MenuItem key={option} value={option}>
                  {option} {/* Display string option directly */}
                </MenuItem>
              )
            ))}
          </TextField>
        </FormControl>
      )}
    />
  );
};

FormDropDown.propTypes = {
  control: PropTypes.object.isRequired,
  formErrors: PropTypes.object,
  label: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.object])).isRequired,
  formFieldId: PropTypes.string.isRequired,
  required: PropTypes.bool,
  readonly: PropTypes.bool,
  testId: PropTypes.string,
  validationOptions: PropTypes.object,
  textFieldProps: PropTypes.object,
  showInlineErrors: PropTypes.bool,
  valueName: PropTypes.string
};

FormDropDown.defaultProps = {
  formErrors: null,
  required: false,
  readonly: false,
  testId: null,
  validationOptions: {},
  textFieldProps: {},
  showInlineErrors: true,
  valueName: undefined
};

export default FormDropDown;
